/**
 * Live counter stream over Server-Sent Events (TZ 8.2 "Jonli hisoblagich", 8.13 "Real vaqt", NFR-03 < 30 s).
 * Reconnects with capped exponential backoff when the browser gives up (readyState CLOSED); the mock backend
 * provides the same EventSource-like interface.
 */
import { useEffect, useRef, useState } from 'react';
import type { LiveEvent } from './api_types';
import { downDoorsOf, isCoverageState } from './store_door_contract';

export interface EventSourceLike {
  readonly readyState: number;
  addEventListener(eventName: 'open' | 'error', listener: () => void): void;
  addEventListener(eventName: 'live', listener: (messageEvent: MessageEvent<string>) => void): void;
  close(): void;
}

export type EventSourceFactory = (streamUrl: string) => EventSourceLike;

export const browserEventSourceFactory: EventSourceFactory = (streamUrl) =>
  new EventSource(streamUrl, { withCredentials: true });

export const EVENT_SOURCE_CLOSED = 2;
export const INITIAL_RECONNECT_DELAY_MS = 1_000;
export const MAX_RECONNECT_DELAY_MS = 30_000;

/** 1 s, 2 s, 4 s ... capped at 30 s. */
export function reconnectDelayMs(failedAttemptCount: number): number {
  return Math.min(MAX_RECONNECT_DELAY_MS, INITIAL_RECONNECT_DELAY_MS * 2 ** Math.max(0, failedAttemptCount));
}

export function parseLiveEvent(rawData: string): LiveEvent | null {
  try {
    const parsedData = JSON.parse(rawData) as Partial<LiveEvent>;
    if (
      typeof parsedData.today_visits !== 'number' ||
      typeof parsedData.updated_at !== 'string' ||
      typeof parsedData.trading_day !== 'string'
    ) {
      return null;
    }
    return {
      store_id: parsedData.store_id ?? null,
      today_visits: parsedData.today_visits,
      last_hour_visits: parsedData.last_hour_visits ?? 0,
      // Unique visitors live (visits - returning, owner 2026-10-05); null without the permission or an older server.
      today_unique: typeof parsedData.today_unique === 'number' ? parsedData.today_unique : null,
      last_hour_unique: typeof parsedData.last_hour_unique === 'number' ? parsedData.last_hour_unique : null,
      trading_day: parsedData.trading_day,
      updated_at: parsedData.updated_at,
      // Multi-door stores (FR-RB-02): the server default is "full" (every door counting).
      coverage_state: isCoverageState(parsedData.coverage_state) ? parsedData.coverage_state : 'full',
      down_doors: downDoorsOf(parsedData.down_doors),
    };
  } catch {
    return null;
  }
}

export type LiveConnectionStatus = 'connecting' | 'open' | 'reconnecting';

export interface LiveStreamState {
  /** Latest event per store id; key "network" holds the company-wide event (store_id null). */
  latestEvents: Record<string, LiveEvent>;
  status: LiveConnectionStatus;
}

export const NETWORK_LIVE_KEY = 'network';

export function useLiveStream(
  createEventSource: EventSourceFactory,
  streamUrl: string,
  enabled: boolean,
): LiveStreamState {
  const [latestEvents, setLatestEvents] = useState<Record<string, LiveEvent>>({});
  const [status, setStatus] = useState<LiveConnectionStatus>('connecting');
  const failedAttemptsRef = useRef(0);

  useEffect(() => {
    if (!enabled) {
      return undefined;
    }
    let eventSource: EventSourceLike | null = null;
    let reconnectTimer: number | null = null;
    let isStopped = false;

    const connect = () => {
      eventSource = createEventSource(streamUrl);
      eventSource.addEventListener('open', () => {
        failedAttemptsRef.current = 0;
        setStatus('open');
      });
      eventSource.addEventListener('live', (messageEvent) => {
        const liveEvent = parseLiveEvent(messageEvent.data);
        if (liveEvent !== null) {
          setLatestEvents((previousEvents) => ({
            ...previousEvents,
            [liveEvent.store_id ?? NETWORK_LIVE_KEY]: liveEvent,
          }));
        }
      });
      eventSource.addEventListener('error', () => {
        if (isStopped || eventSource === null) {
          return;
        }
        setStatus('reconnecting');
        if (eventSource.readyState === EVENT_SOURCE_CLOSED) {
          eventSource.close();
          const delayMs = reconnectDelayMs(failedAttemptsRef.current);
          failedAttemptsRef.current += 1;
          reconnectTimer = window.setTimeout(connect, delayMs);
        }
      });
    };

    connect();
    return () => {
      isStopped = true;
      if (reconnectTimer !== null) {
        window.clearTimeout(reconnectTimer);
      }
      eventSource?.close();
    };
  }, [createEventSource, streamUrl, enabled]);

  return { latestEvents, status };
}
